import East from "@mui/icons-material/East";
import { Button, Tooltip, type SxProps, type Theme } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useTenantId } from "hooks/useTenant";
import {
  useChatSurfaceCoordinator,
  type ScoutQuestionRequest,
} from "providers/ChatSurfaceCoordinator";
import { useCallback } from "react";

import { useScoutAccessPackage } from "../scoutAccess";

export type PopoverScoutTriggerProps = ScoutQuestionRequest & {
  label?: string;
  tooltip?: string;
  /** Overrides the label as the accessible name when several buttons share it. */
  ariaLabel?: string;
  /** DOM id, for callers that spotlight the button in a product tour. */
  id?: string;
  /** Placement-owned styling (color, spacing). The Scout mark follows `color`. */
  sx?: SxProps<Theme>;
};

/**
 * "Ask Scout" button that hands its question to the top-nav popover rather than
 * opening one of its own, so answers always appear where Scout already opens.
 * Renders nothing when Scout is unavailable, so callers need no gate.
 */
export function PopoverScoutTrigger({
  question,
  context,
  label = "Ask Scout",
  tooltip = "Ask Scout",
  ariaLabel,
  id,
  sx,
}: PopoverScoutTriggerProps) {
  const chatSurfaceCoordinator = useChatSurfaceCoordinator();
  const { scoutAccessPackage } = useScoutAccessPackage();
  const tenantId = useTenantId();

  const handleClick = useCallback(() => {
    chatSurfaceCoordinator?.openScoutWithQuestion({
      question,
      ...(context ? { context } : {}),
    });
  }, [chatSurfaceCoordinator, context, question]);

  // Same conditions the top-nav launcher hides on.
  if (!scoutAccessPackage || !tenantId) {
    return null;
  }

  return (
    <Tooltip title={tooltip}>
      <Button
        {...(id ? { id } : {})}
        onClick={handleClick}
        size="small"
        variant="text"
        {...(sx ? { sx } : {})}
        // A caller-set color covers the mark too; on its own it stays brand pumpkin.
        startIcon={<ScoutIcon fontSize="small" {...(sx ? { sx: { color: "inherit" } } : {})} />}
        endIcon={<East fontSize="small" />}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
